import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

function numLocale(lang) {
    if (lang === "en") return "en-US";
    if (lang === "fr") return "fr-FR";
    return "it-IT";
}

/**
 * Commission Calculator — indicative only.
 * User picks: monthly bookings + average ticket + category.
 * Qualitative simulator: shows which bookings carry a commission (no rates).
 */

const AVG_TICKETS = [
    { id: "small", label: "€ 30", value: 30 },
    { id: "mid", label: "€ 60", value: 60 },
    { id: "big", label: "€ 120", value: 120 },
    { id: "xl", label: "€ 250", value: 250 },
];

function CalculatorControls({ t, bookings, setBookings, ticket, setTicket, showNoshow, setShowNoshow }) {
    return (
        <div className="itly-card" style={{ padding: "28px 30px" }} data-testid="commission-controls">
            <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>
                    {t("calc.bookings")}
                    <strong style={{ color: "var(--itly-fg-strong)", marginLeft: 10, fontFamily: "Fraunces, serif", fontSize: 22 }}>
                        {bookings}
                    </strong>
                </label>
                <input
                    type="range"
                    min={5}
                    max={200}
                    step={5}
                    value={bookings}
                    onChange={(e) => setBookings(Number(e.target.value))}
                    className="itly-range"
                    data-testid="commission-bookings"
                    style={{ width: "100%", marginTop: 10 }}
                />
                <div style={rangeTicksStyle}>
                    <span>5</span><span>50</span><span>100</span><span>150</span><span>200</span>
                </div>
            </div>

            <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>{t("calc.ticket")}</label>
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }} data-testid="commission-ticket-picker">
                    {AVG_TICKETS.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTicket(t.id)}
                            className={`itly-chip-light ${ticket === t.id ? "on" : ""}`}
                            data-testid={`commission-ticket-${t.id}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label style={labelStyle}>
                    {t("calc.noshow")}
                    <strong style={{ color: "var(--itly-fg-strong)", marginLeft: 10, fontFamily: "Fraunces, serif", fontSize: 22 }}>
                        {showNoshow}%
                    </strong>
                </label>
                <input
                    type="range"
                    min={0}
                    max={50}
                    step={5}
                    value={showNoshow}
                    onChange={(e) => setShowNoshow(Number(e.target.value))}
                    className="itly-range"
                    data-testid="commission-noshow"
                    style={{ width: "100%", marginTop: 10 }}
                />
                <div style={rangeTicksStyle}>
                    <span>0%</span><span>25%</span><span>50%</span>
                </div>
            </div>
        </div>
    );
}

function CalculatorOutput({ t, lang, successful, revenue }) {
    return (
        <div
            style={{
                background: "var(--itly-teal-900)",
                color: "var(--itly-on-teal)",
                borderRadius: 16,
                padding: "32px 32px 28px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 20,
                position: "relative",
                overflow: "hidden",
            }}
            data-testid="commission-output"
        >
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: -80,
                    right: -80,
                    width: 220,
                    height: 220,
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(232,113,76,0.24) 0%, rgba(232,113,76,0) 70%)",
                }}
            />
            <div>
                <p style={{ margin: 0, fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--itly-orange-500)" }}>
                    {t("calc.output.badge")}
                </p>
                <div style={{ marginTop: 8, display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                    <span
                        className="itly-mini-count"
                        key={successful}
                        style={{
                            fontFamily: "Fraunces, serif",
                            fontWeight: 700,
                            fontSize: "clamp(46px, 5vw, 68px)",
                            lineHeight: 1,
                            letterSpacing: "-0.03em",
                            color: "var(--itly-on-teal)",
                        }}
                        data-testid="commission-fee"
                    >
                        {successful}
                    </span>
                </div>
                <p style={{ margin: "8px 0 0", fontFamily: "Inter, sans-serif", fontSize: 13, color: "rgba(247,244,238,0.66)" }}>
                    {t("calc.output.rangeNote")}
                </p>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 12,
                    paddingTop: 20,
                    borderTop: "1px solid rgba(247,244,238,0.14)",
                }}
            >
                <div>
                    <span style={outputLabelStyle}>{t("calc.output.successful")}</span>
                    <span style={outputValueStyle} data-testid="commission-successful">{successful}</span>
                </div>
                <div>
                    <span style={outputLabelStyle}>{t("calc.output.noshowCost")}</span>
                    <span style={{ ...outputValueStyle, color: "var(--itly-orange-500)" }} data-testid="commission-noshow-cost">
                        € 0
                    </span>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                    <span style={outputLabelStyle}>{t("calc.output.revenue")}</span>
                    <span style={outputValueStyle}>€ {revenue.toLocaleString(numLocale(lang))}</span>
                </div>
            </div>

            <p style={{ margin: 0, fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "rgba(247,244,238,0.55)", lineHeight: 1.5 }}>
                {t("calc.disclaimer")}
            </p>
        </div>
    );
}

export default function CommissionCalculator() {
    const { t, lang } = useI18n();
    const [bookings, setBookings] = useState(40);
    const [ticket, setTicket] = useState("mid");
    const [showNoshow, setShowNoshow] = useState(15); // % no-show

    const avg = AVG_TICKETS.find((t) => t.id === ticket).value;
    const successful = Math.max(0, Math.round(bookings * (1 - showNoshow / 100)));
    const revenue = successful * avg;

    return (
        <section
            id="commissione"
            data-testid="commission-calc"
            style={{
                background: "var(--itly-surface)",
                borderTop: "1px solid var(--itly-border)",
                borderBottom: "1px solid var(--itly-border)",
                padding: "96px 32px",
            }}
            data-reveal="up"
        >
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                <div style={{ maxWidth: 780, marginBottom: 40 }}>
                    <p
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 12.5,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "var(--itly-orange-700)",
                            margin: 0,
                        }}
                    >
                        {t("calc.badge")}
                    </p>
                    <h2
                        className="itly-serif"
                        style={{
                            marginTop: 14,
                            marginBottom: 0,
                            fontSize: "clamp(34px, 3.8vw, 52px)",
                            lineHeight: 1.06,
                            color: "var(--itly-fg-strong)",
                            letterSpacing: "-0.022em",
                        }}
                    >
                        {t("calc.title_a")}{" "}
                        <em style={{ fontStyle: "italic", color: "var(--itly-orange-500)", fontWeight: 500 }}>
                            {t("calc.title_b")}
                        </em>
                    </h2>
                    <p
                        style={{
                            marginTop: 18,
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            color: "var(--itly-muted)",
                            lineHeight: 1.6,
                            maxWidth: 640,
                        }}
                    >
                        {t("calc.sub")}
                    </p>
                </div>

                <div
                    className="itly-commission-grid"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                        gap: 24,
                        alignItems: "stretch",
                    }}
                >
                    {/* Controls */}
                    <CalculatorControls
                        t={t}
                        bookings={bookings}
                        setBookings={setBookings}
                        ticket={ticket}
                        setTicket={setTicket}
                        showNoshow={showNoshow}
                        setShowNoshow={setShowNoshow}
                    />

                    {/* Output */}
                    <CalculatorOutput t={t} lang={lang} successful={successful} revenue={revenue} />
                </div>
            </div>
        </section>
    );
}

const labelStyle = {
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--itly-fg-strong)",
    letterSpacing: "0.005em",
    display: "flex",
    alignItems: "center",
};
const rangeTicksStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 6,
    fontFamily: "Inter, sans-serif",
    fontSize: 11.5,
    color: "var(--itly-muted)",
    letterSpacing: "0.06em",
};
const outputLabelStyle = {
    display: "block",
    fontFamily: "Inter, sans-serif",
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "rgba(247,244,238,0.55)",
};
const outputValueStyle = {
    display: "block",
    marginTop: 4,
    fontFamily: "Fraunces, serif",
    fontWeight: 700,
    fontSize: 22,
    letterSpacing: "-0.015em",
    color: "var(--itly-on-teal)",
};
